import { useEffect, useRef, useState, useCallback } from 'react'

declare global {
  interface Window {
    myCallbackFunction?: (token: string) => void
    xxTrustedFormCertUrl?: any
    xxTrustedFormToken?: string
    xxTrustedFormPingUrl?: any
  }
}

/**
 * Module-level cache. In a single-page app the injected scripts survive route
 * changes, so a second mount would otherwise skip injection and never see the
 * callback fire again. Caching the captured values means any later page in the
 * funnel gets them immediately.
 */
let cachedLeadIdToken = ''
let cachedTrustedFormUrl = ''

function isCertUrl(val: unknown): val is string {
  return (
    typeof val === 'string' &&
    val.indexOf('https://cert.trustedform.com/') === 0
  )
}

/** Jornaya tokens are UUIDs. Guard against reading back an empty input. */
function isLeadIdToken(val: unknown): val is string {
  return (
    typeof val === 'string' &&
    /^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/i.test(val)
  )
}

function scanForLeadIdToken(): string {
  const inputs = document.querySelectorAll('input[name="leadid_token"]')
  for (let i = 0; i < inputs.length; i++) {
    const el = inputs[i] as HTMLInputElement
    if (isLeadIdToken(el.value)) return el.value
  }
  return ''
}

function scanForTrustedFormUrl(): string {
  // 1. Check all inputs with the TrustedForm field name
  const inputs = document.querySelectorAll('input[name="xxTrustedFormCertUrl"]')
  for (let i = 0; i < inputs.length; i++) {
    const el = inputs[i] as HTMLInputElement
    if (isCertUrl(el.value)) return el.value
  }

  // 2. Check window.xxTrustedFormCertUrl (could be string, element, or other)
  const winCert = window.xxTrustedFormCertUrl
  if (winCert) {
    if (isCertUrl(winCert)) return winCert
    if (winCert instanceof HTMLInputElement && isCertUrl(winCert.value))
      return winCert.value
    // Some versions store it as .value on a plain object
    if (typeof winCert === 'object' && winCert !== null && 'value' in winCert) {
      const v = winCert.value
      if (isCertUrl(v)) return v
    }
  }

  // 3. Check window.xxTrustedFormToken
  if (isCertUrl(window.xxTrustedFormToken)) return window.xxTrustedFormToken

  // 4. Check window.xxTrustedFormPingUrl (some versions use this)
  const winPing = window.xxTrustedFormPingUrl
  if (winPing) {
    if (isCertUrl(winPing)) return winPing
    if (winPing instanceof HTMLInputElement && isCertUrl(winPing.value))
      return winPing.value
  }

  // 5. Scan ALL hidden inputs for any cert URL
  const allHidden = document.querySelectorAll('input[type="hidden"]')
  for (let i = 0; i < allHidden.length; i++) {
    const el = allHidden[i] as HTMLInputElement
    if (isCertUrl(el.value)) return el.value
  }

  // 6. Check for TrustedForm iframe (it creates one) and look for data attributes
  const tfIframe = document.querySelector(
    'iframe[src*="trustedform"]',
  ) as HTMLIFrameElement | null
  if (tfIframe) {
    const certFromAttr = tfIframe.getAttribute('data-tf-cert-url')
    if (isCertUrl(certFromAttr)) return certFromAttr!
  }

  return ''
}

/**
 * Current compliance values, readable outside React. The API layer calls these
 * at request time so a cert that landed after a component rendered is still
 * sent, and a value cached earlier in the funnel is still found after the
 * input element has been replaced by a re-render.
 */
export function readTrustedFormCert(): string {
  const found = scanForTrustedFormUrl()
  if (found) {
    cachedTrustedFormUrl = found
    return found
  }
  return cachedTrustedFormUrl
}

export function readLeadIdToken(): string {
  const found = scanForLeadIdToken()
  if (found) {
    cachedLeadIdToken = found
    return found
  }
  return cachedLeadIdToken
}

export function useLeadTracking() {
  // Seed from the cache so a later page in the funnel has the values on mount.
  const [leadIdToken, setLeadIdToken] = useState(cachedLeadIdToken)
  const [trustedFormUrl, setTrustedFormUrl] = useState(cachedTrustedFormUrl)
  const initialized = useRef(false)
  const foundCert = useRef(!!cachedTrustedFormUrl)
  const foundLead = useRef(!!cachedLeadIdToken)

  const onCertFound = useCallback((url: string) => {
    if (foundCert.current) return
    foundCert.current = true
    cachedTrustedFormUrl = url
    console.log('[LeadTracking] ✅ TrustedForm cert URL captured:', url)
    setTrustedFormUrl(url)
    // Mirror into every cert input so the value is readable at submit time
    // even if a re-render replaced the element TrustedForm originally wrote to.
    document
      .querySelectorAll<HTMLInputElement>('input[name="xxTrustedFormCertUrl"]')
      .forEach((el) => {
        el.value = url
      })
  }, [])

  const onLeadIdFound = useCallback((token: string) => {
    if (foundLead.current) return
    foundLead.current = true
    cachedLeadIdToken = token
    console.log('[LeadTracking] ✅ Jornaya LeadiD token captured:', token)
    setLeadIdToken(token)
    document
      .querySelectorAll<HTMLInputElement>('input[name="leadid_token"]')
      .forEach((el) => {
        el.value = token
      })
  }, [])

  useEffect(() => {
    if (initialized.current) return
    initialized.current = true

    // --- Find a form element to attach hidden inputs to ---
    const formEl = document.querySelector('form') || document.body

    // A page may declare its own hidden inputs in markup. If a value was
    // already captured earlier in the funnel, seed those inputs now — the
    // scripts only write once, so a fresh element would otherwise stay empty.
    if (cachedLeadIdToken) {
      document
        .querySelectorAll<HTMLInputElement>('input[name="leadid_token"]')
        .forEach((el) => {
          if (!el.value) el.value = cachedLeadIdToken
        })
    }
    if (cachedTrustedFormUrl) {
      document
        .querySelectorAll<HTMLInputElement>(
          'input[name="xxTrustedFormCertUrl"]',
        )
        .forEach((el) => {
          if (!el.value) el.value = cachedTrustedFormUrl
        })
    }

    // --- Create hidden input for LeadID ---
    if (!document.querySelector('input[name="leadid_token"]')) {
      const leadInput = document.createElement('input')
      leadInput.type = 'hidden'
      leadInput.name = 'leadid_token'
      leadInput.id = 'leadid_token'
      leadInput.value = cachedLeadIdToken
      formEl.appendChild(leadInput)
    }

    // --- Create hidden input for TrustedForm if not already in DOM ---
    if (!document.querySelector('input[name="xxTrustedFormCertUrl"]')) {
      const tfInput = document.createElement('input')
      tfInput.type = 'hidden'
      tfInput.name = 'xxTrustedFormCertUrl'
      tfInput.id = 'xxTrustedFormCertUrl'
      tfInput.value = cachedTrustedFormUrl
      formEl.appendChild(tfInput)
      console.log(
        '[LeadTracking] Created hidden input for TrustedForm inside',
        formEl.tagName,
      )
    }

    // --- Jornaya LeadiD script ---
    // Always (re)register the callback: the script tag survives SPA route
    // changes, but the callback reference must point at the live handler.
    window.myCallbackFunction = (token: string) => {
      onLeadIdFound(token)
    }

    if (!document.getElementById('LeadiDscript_campaign')) {
      const leadIdScript = document.createElement('script')
      leadIdScript.id = 'LeadiDscript_campaign'
      leadIdScript.type = 'text/javascript'
      leadIdScript.async = true
      leadIdScript.src =
        '//create.lidstatic.com/campaign/839074cd-3bde-b295-6b35-42ba16e8d9e1.js?snippet_version=2&callback=myCallbackFunction'
      leadIdScript.onerror = () => {
        console.error(
          '[LeadTracking] ❌ Jornaya LeadiD script FAILED to load (blocked or bad campaign id)',
        )
      }
      document.body.appendChild(leadIdScript)

      // Jornaya's noscript pixel, for visitors with JS-based tracking blocked.
      if (!document.getElementById('LeadiDnoscript')) {
        const noscript = document.createElement('noscript')
        noscript.id = 'LeadiDnoscript'
        const img = document.createElement('img')
        img.src =
          '//create.leadid.com/noscript.gif?lac=FB53B415-BE7F-F6FE-B80A-846EF010C0D2&lck=839074cd-3bde-b295-6b35-42ba16e8d9e1&snippet_version=2'
        img.alt = ''
        noscript.appendChild(img)
        document.body.appendChild(noscript)
      }
    } else {
      console.log(
        '[LeadTracking] Jornaya script already present — reusing existing session',
      )
    }

    // --- TrustedForm Script ---
    function loadTrustedForm() {
      const field = 'xxTrustedFormCertUrl'
      const provideReferrer = false
      const tf = document.createElement('script')
      tf.id = 'TrustedFormScript'
      tf.type = 'text/javascript'
      tf.async = true
      tf.src =
        'http' +
        (document.location.protocol === 'https:' ? 's' : '') +
        '://api.trustedform.com/trustedform.js?provide_referrer=' +
        escape(String(provideReferrer)) +
        '&field=' +
        escape(field) +
        '&l=' +
        new Date().getTime() +
        Math.random()

      tf.onload = () => {
        console.log('[LeadTracking] TrustedForm script loaded successfully')
        setTimeout(() => {
          const url = scanForTrustedFormUrl()
          if (url) onCertFound(url)
        }, 1000)
      }

      tf.onerror = (err) => {
        console.error(
          '[LeadTracking] ❌ TrustedForm script FAILED to load:',
          err,
        )
      }

      const s = document.getElementsByTagName('script')[0]
      if (s && s.parentNode) {
        s.parentNode.insertBefore(tf, s)
      } else {
        document.head.appendChild(tf)
      }
    }

    if (!document.getElementById('TrustedFormScript')) {
      loadTrustedForm()
    }

    // --- Polling fallback for both providers ---
    // Covers a callback that fires before this handler is registered, and a
    // script that writes straight to its hidden input without calling back.
    let pollCount = 0
    const pollInterval = setInterval(() => {
      if (!foundCert.current) {
        const url = scanForTrustedFormUrl()
        if (url) onCertFound(url)
      }
      if (!foundLead.current) {
        const token = scanForLeadIdToken()
        if (token) onLeadIdFound(token)
      }

      if (foundCert.current && foundLead.current) {
        clearInterval(pollInterval)
        return
      }

      pollCount++
      if (pollCount % 20 === 0) {
        console.log(
          `[LeadTracking] Still polling (${pollCount * 500}ms) — cert: ${foundCert.current ? 'ok' : 'pending'}, leadid: ${foundLead.current ? 'ok' : 'pending'}`,
        )
      }
    }, 500)

    const timeout = setTimeout(() => {
      clearInterval(pollInterval)
      if (!foundCert.current) {
        console.warn(
          '[LeadTracking] ⚠️ TrustedForm cert URL not found after 60s',
        )
      }
      if (!foundLead.current) {
        console.warn(
          '[LeadTracking] ⚠️ Jornaya LeadiD token not found after 60s',
        )
      }
    }, 60000)

    return () => {
      clearInterval(pollInterval)
      clearTimeout(timeout)
    }
  }, [onCertFound, onLeadIdFound])

  return { leadIdToken, trustedFormUrl }
}
